import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { formatInCurrency } from '../lib/format';
import { conversionFactor, useCurrencyStore } from '../lib/currency-store';

interface PublicBilling {
  baseCurrency: string;
  currencies: { code: string; rate: number }[];
  ratesUpdatedAt: string;
}

/**
 * The currency plan prices are entered in — set by the platform owner in
 * Settings → Payments. Every plan price the panel prints goes through
 * `format` so the symbol follows that setting rather than a hard-coded ₹.
 */
export function useBaseCurrency() {
  const query = useQuery({
    queryKey: ['public', 'billing'],
    queryFn: () => api.get<PublicBilling>('/public/billing', { anonymous: true }),
    staleTime: 60_000,
  });
  const code = query.data?.baseCurrency ?? 'INR';
  const currencies = query.data?.currencies ?? [];

  // Subscribed, not read: `format` runs during render, so the chosen currency
  // has to be a value React is watching. Reading it from the module instead
  // left the Plans page printing dollars after the app bar had moved to GBP —
  // nothing told the page to render again.
  const view = useCurrencyStore((state) => state.code);

  return {
    code,
    currencies,
    loading: query.isLoading,
    /**
     * Plan prices are held in the base currency and follow the app bar's
     * currency switch like every other amount. Without this the switch changed
     * the dues on one page and left the plan price beside it in dollars, which
     * reads as a bug. The figure is the amount at the platform's daily rate —
     * the rate the checkout quotes with — so it is shown plainly rather than
     * hedged.
     */
    format: (paise: bigint | string | number, options: { decimals?: boolean; exact?: boolean } = {}) => {
      if (!options.exact && view && view !== code) {
        const rate = conversionFactor({
          own: code,
          code: view,
          base: code,
          rates: Object.fromEntries(currencies.map((entry) => [entry.code, entry.rate])),
        });
        if (rate) return formatInCurrency(paise, view, rate, options);
      }
      return formatInCurrency(paise, code, 1, options);
    },
  };
}
